import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import server from '../../../api/server';
import { fetchApplication } from '../../../actions';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import BankStatementsApplicant from './BankStatementsApplicant';
import { BankIcon, DownloadIcon } from '../ui/icons';

// Redesign of newadmin/BankStatementCollection.js, extended to support a
// per-application provider: illion (the embedded bankstatements.com.au portal,
// unchanged), Frollo (client connects via Frollo's hosted link — tracked by the
// server/modules/frollo.js inbound-email pipeline), or both.
const PROVIDERS = [
    { value: 'illion', label: 'illion', desc: 'Embedded portal — clients connect their bank inside LoanGateway.' },
    { value: 'frollo', label: 'Frollo', desc: 'Clients create a Frollo account via a secure link, then share their data.' },
    { value: 'both', label: 'Both', desc: 'Offer illion and Frollo — the client picks whichever they prefer.' },
];

const providerIncludes = (provider, name) => provider === name || provider === 'both';

const formatDate = (value) => {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const FrolloStatusPanel = ({ application }) => {
    const applicants = application.applicants || {};
    const keys = Object.keys(applicants);
    return (
        <div className="border-t border-[#F0F1F5] pt-4 mt-4">
            <p className="text-xs font-bold uppercase tracking-wide text-[#6B7280] mb-2">Frollo</p>
            <ul className="divide-y divide-[#F0F1F5]">
                {keys.length === 0 ? (
                    <li className="py-3 text-sm text-[#6B7280]">No applicants yet.</li>
                ) : (
                    keys.map((key) => {
                        const a = applicants[key] || {};
                        const received = !!a.frollo_received;
                        return (
                            <li key={key} className="flex items-center justify-between gap-3 py-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-[#1F2937]">{a.name || 'Applicant'}</p>
                                    <p className="text-xs text-[#6B7280]">
                                        {received && a.frollo_received_at
                                            ? `Connected ${formatDate(a.frollo_received_at)}`
                                            : 'Waiting for the client to connect their bank.'}
                                    </p>
                                </div>
                                {received ? (
                                    <Badge tone="success">Connected</Badge>
                                ) : (
                                    <Badge tone="warning">Awaiting</Badge>
                                )}
                            </li>
                        );
                    })
                )}
            </ul>
        </div>
    );
};

const BankStatementsSection = () => {
    const application = useSelector((s) => s.admin.active_application.application);
    const bankstatements = useSelector((s) => s.admin.active_application.bankstatements);
    const user = useSelector((s) => s.user);
    const dispatch = useDispatch();

    const applicationId = application.id;
    const savedProvider = application.bank_statement_provider || 'illion';
    const requested = !!(application.requested_info && application.requested_info.bankstatements);

    const [selected, setSelected] = useState(savedProvider);
    const [saving, setSaving] = useState(false);

    // Port of the old create flow, generalised: persists the chosen provider,
    // lazily creates the illion object when illion/both is picked, and refreshes
    // the whole active application so provider + object land in the store.
    const submit = async () => {
        setSaving(true);
        await server.post('/bankstatements/provider', {
            id: user.token,
            application_id: applicationId,
            provider: selected,
        });
        dispatch(fetchApplication(applicationId));
        setSaving(false);
    };

    const downloadAll = async () => {
        const response = await server.post(
            '/bankstatements/applicant/all',
            {
                id: user.token,
                path: `${applicationId}`,
                bankstatement_id: bankstatements.id,
            },
            { responseType: 'blob' }
        );
        let blob = new Blob([response.data], { type: 'application/zip' });
        let objectUrl = URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = objectUrl;
        link.download = `${application.name}_bank_statements.zip`;
        link.click();
    };

    const showIllion = requested && providerIncludes(savedProvider, 'illion');
    const showFrollo = requested && providerIncludes(savedProvider, 'frollo');
    const dirty = selected !== savedProvider;
    const primaryLabel = !requested ? 'Request Bank Statements' : dirty ? 'Update Method' : 'Requested';

    return (
        <Card
            title="Bank Statements"
            icon={<BankIcon className="h-5 w-5" />}
            actions={
                showIllion && bankstatements ? (
                    <Button variant="secondary" onClick={downloadAll}>
                        <DownloadIcon className="h-4 w-4" />
                        Download All
                    </Button>
                ) : null
            }
        >
            <div className={showIllion || showFrollo ? 'mb-1' : ''}>
                <p className="text-xs font-bold uppercase tracking-wide text-[#6B7280] mb-2">Collection method</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {PROVIDERS.map((p) => {
                        const active = selected === p.value;
                        return (
                            <button
                                key={p.value}
                                type="button"
                                onClick={() => setSelected(p.value)}
                                className={`text-left rounded-xl border-2 px-4 py-3 transition-colors ${
                                    active ? 'border-[#7C5CFC] bg-[#EFEAFE]' : 'border-[#E5E7EB] bg-white hover:border-[#C4B5FD]'
                                }`}
                            >
                                <span className="flex items-center justify-between gap-2">
                                    <span className="text-sm font-bold text-[#1F2937]">{p.label}</span>
                                    {requested && savedProvider === p.value ? <Badge tone="accent">Active</Badge> : null}
                                </span>
                                <span className="mt-1 block text-xs text-[#6B7280] leading-snug">{p.desc}</span>
                            </button>
                        );
                    })}
                </div>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <Button onClick={submit} disabled={saving || (requested && !dirty)}>
                        {saving ? 'Saving…' : primaryLabel}
                    </Button>
                    {requested ? (
                        <span className="text-xs text-[#6B7280]">The client's dashboard updates to match the selected method.</span>
                    ) : (
                        <span className="text-xs text-[#6B7280]">This turns on the bank statements task on the client dashboard.</span>
                    )}
                </div>
            </div>

            {showIllion && bankstatements ? (
                <div className="border-t border-[#F0F1F5] pt-4 mt-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-[#6B7280] mb-1">illion portal</p>
                    {Object.keys(bankstatements.applicants).map((key) => (
                        <BankStatementsApplicant key={key} applicantId={key} />
                    ))}
                </div>
            ) : null}

            {showFrollo ? <FrolloStatusPanel application={application} /> : null}
        </Card>
    );
};

export default BankStatementsSection;
